/**
 * Layout Query & Mutation Utilities
 * 
 * Pure functions for querying and mutating rc-dock layout data immutably.
 * All mutation functions return new objects (immutable pattern).
 */
import type { LayoutBase, BoxBase, PanelBase, BoxData, PanelData, TabData, TabBase } from "rc-dock";

// ============================================================================
// QUERY FUNCTIONS (read-only)
// ============================================================================

/**
 * Recursively collect all tabs from a layout box or panel.
 */
export function collectTabs(data: PanelData | BoxData | undefined): TabData[] {
  let result: TabData[] = [];

  if (!data) return result;

  // If it's a panel with tabs
  if ("tabs" in data && data.tabs) {
    for (const tab of data.tabs) {
      result.push(tab);
    }
  }

  // If it's a box with children
  if ("children" in data && data.children) {
    for (const child of data.children) {
      result = [...result, ...collectTabs(child as PanelData | BoxData)];
    }
  }

  return result;
}

/**
 * Recursively collect all panels from a box or panel.
 */
function collectPanelsFromBox(data: PanelData | BoxData | undefined): PanelData[] {
  let result: PanelData[] = [];

  if (!data) return result;

  // If it's a box with children, recurse
  if ("children" in data && data.children) {
    for (const child of data.children) {
      result = [...result, ...collectPanelsFromBox(child as PanelData | BoxData)];
    }
  } else if ("tabs" in data) {
    // It's a panel
    result.push(data as PanelData);
  }

  return result;
}

/**
 * Collect all panels from a layout.
 * Searches dockbox, floatbox, windowbox, and maxbox.
 */
export function collectPanels(layout: LayoutBase): PanelData[] {
  return [
    ...collectPanelsFromBox(layout.dockbox as BoxData),
    ...collectPanelsFromBox(layout.floatbox as BoxData),
    ...collectPanelsFromBox(layout.windowbox as BoxData),
    ...collectPanelsFromBox(layout.maxbox as BoxData),
  ];
}

/**
 * Find a specific tab by ID in the layout.
 * Searches dockbox, floatbox, windowbox, and maxbox.
 */
export function findTabById(layout: LayoutBase, tabId: string): TabData | null {
  const allTabs = [
    ...collectTabs(layout.dockbox as BoxData),
    ...collectTabs(layout.floatbox as BoxData),
    ...collectTabs(layout.windowbox as BoxData),
    ...collectTabs(layout.maxbox as BoxData),
  ];
  return allTabs.find(t => t.id === tabId) ?? null;
}

/**
 * Find the panel containing a specific tab.
 */
export function findPanelByTabId(layout: LayoutBase, tabId: string): PanelData | null {
  const panels = collectPanels(layout);
  for (const panel of panels) {
    if (panel.tabs?.some(t => t.id === tabId)) {
      return panel;
    }
  }
  return null;
}

/**
 * Get the currently active tab ID from the layout.
 * Looks for the first panel with an activeId set.
 */
export function getActiveTabId(layout: LayoutBase): string | null {
  const panels = collectPanels(layout);
  for (const panel of panels) {
    if (panel.activeId) {
      return panel.activeId;
    }
  }
  // Fallback: return first tab of first panel
  if (panels.length > 0 && panels[0].tabs && panels[0].tabs.length > 0) {
    return panels[0].tabs[0].id ?? null;
  }
  return null;
}

/**
 * Get the maximized tab ID from layout.maxbox.
 * Returns null if nothing is maximized.
 */
export function getMaximizedTabId(layout: LayoutBase): string | null {
  if (!layout.maxbox) return null;
  
  const panels = collectPanelsFromBox(layout.maxbox as BoxData);
  for (const panel of panels) {
    if (panel.activeId) {
      return panel.activeId;
    }
    // Fallback to first tab
    if (panel.tabs && panel.tabs.length > 0) {
      return panel.tabs[0].id ?? null;
    }
  }
  return null;
}

/**
 * Get all tab IDs as an array.
 * Searches dockbox, floatbox, windowbox, and maxbox.
 */
export function getAllTabIds(layout: LayoutBase): string[] {
  const tabs = [
    ...collectTabs(layout.dockbox as BoxData),
    ...collectTabs(layout.floatbox as BoxData),
    ...collectTabs(layout.windowbox as BoxData),
    ...collectTabs(layout.maxbox as BoxData),
  ];
  return tabs.filter(t => !!t.id).map(t => t.id!);
}

/**
 * Check if a tab exists in the layout.
 */
export function isTabOpen(layout: LayoutBase, tabId: string): boolean {
  return findTabById(layout, tabId) !== null;
}

/**
 * Find the first panel in the layout (for adding tabs when no target specified).
 */
export function findFirstPanel(layout: LayoutBase): PanelData | null {
  const panels = collectPanels(layout);
  return panels.length > 0 ? panels[0] : null;
}

/**
 * Find a panel by its ID.
 */
export function findPanelById(layout: LayoutBase, panelId: string): PanelData | null {
  const panels = collectPanels(layout);
  return panels.find(p => p.id === panelId) ?? null;
}

// ============================================================================
// MUTATION FUNCTIONS (return new layout, immutable)
// ============================================================================

/**
 * Deep clone a layout structure.
 * rc-dock mutates layouts, so we need fresh copies.
 */
function cloneLayout(layout: LayoutBase): LayoutBase {
  return JSON.parse(JSON.stringify(layout));
}

/**
 * Recursively update a panel in a box structure.
 */
function updatePanelInBox(
  box: BoxData,
  panelId: string,
  updater: (panel: PanelData) => PanelData
): BoxData {
  const newChildren = box.children.map(child => {
    if ("tabs" in child) {
      // It's a panel
      const panel = child as PanelData;
      if (panel.id === panelId) {
        return updater(panel);
      }
      return panel;
    } else {
      // It's a box, recurse
      return updatePanelInBox(child as BoxData, panelId, updater);
    }
  });

  return { ...box, children: newChildren };
}

/**
 * Add a tab to a specific panel (returns new layout).
 * Accepts TabBase (only id required) - loadTab fills in title/content.
 */
export function addTabToPanel(
  layout: LayoutBase,
  tab: TabBase,
  panelId: string
): LayoutBase {
  const newLayout = cloneLayout(layout);
  
  if (!newLayout.dockbox) return newLayout;

  newLayout.dockbox = updatePanelInBox(
    newLayout.dockbox as BoxData,
    panelId,
    (panel) => ({
      ...panel,
      // Cast TabBase to TabData - loadTab will fill in title/content
      tabs: [...(panel.tabs || []), tab as TabData],
      activeId: tab.id, // Make the new tab active
    })
  );

  return newLayout;
}

/**
 * Remove a tab from the layout (returns new layout).
 * Note: Does not handle empty panel cleanup - rc-dock does that.
 */
export function removeTab(layout: LayoutBase, tabId: string): LayoutBase {
  const newLayout = cloneLayout(layout);
  
  if (!newLayout.dockbox) return newLayout;

  const removePanelTab = (box: BoxData): BoxData => {
    const newChildren = box.children.map(child => {
      if ("tabs" in child) {
        const panel = child as PanelData;
        const newTabs = panel.tabs?.filter(t => t.id !== tabId) || [];
        const newActiveId = panel.activeId === tabId 
          ? (newTabs[0]?.id ?? undefined)
          : panel.activeId;
        return { ...panel, tabs: newTabs, activeId: newActiveId };
      } else {
        return removePanelTab(child as BoxData);
      }
    });
    return { ...box, children: newChildren };
  };

  newLayout.dockbox = removePanelTab(newLayout.dockbox as BoxData);
  return newLayout;
}

/**
 * Set a tab as active in its panel (returns new layout).
 */
export function setActiveTab(layout: LayoutBase, tabId: string): LayoutBase {
  const panel = findPanelByTabId(layout, tabId);
  if (!panel?.id) return layout;

  const newLayout = cloneLayout(layout);
  
  if (!newLayout.dockbox) return newLayout;

  newLayout.dockbox = updatePanelInBox(
    newLayout.dockbox as BoxData,
    panel.id,
    (p) => ({ ...p, activeId: tabId })
  );

  return newLayout;
}

/**
 * Create a maximized layout from a panel.
 * Moves the panel content to maxbox structure.
 */
export function maximizePanel(layout: LayoutBase, panelId: string): LayoutBase {
  const panel = findPanelById(layout, panelId);
  if (!panel) return layout;

  const newLayout = cloneLayout(layout);
  
  // Create maxbox with the panel's tabs
  newLayout.maxbox = {
    mode: "horizontal",
    children: [
      {
        id: `max-${panelId}`,
        tabs: panel.tabs ? [...panel.tabs] : [],
        activeId: panel.activeId,
        group: "default",
      } as PanelData,
    ],
  } as BoxData;

  return newLayout;
}

/**
 * Clear maximized state (returns layout with maxbox removed).
 * Note: Typically you'd just set maximizedLayout to null in the state,
 * but this utility is here for completeness.
 */
export function clearMaximize(layout: LayoutBase): LayoutBase {
  const newLayout = cloneLayout(layout);
  delete newLayout.maxbox;
  return newLayout;
}

// ============================================================================
// SPLIT UTILITIES (pure, JSON-safe — no parent pointers)
// ============================================================================

type Placement = "split-right" | "split-left" | "split-top" | "split-bottom";
type Scope = "parent" | "root";

function placementToMode(placement: Placement): "horizontal" | "vertical" {
  return placement === "split-left" || placement === "split-right" ? "horizontal" : "vertical";
}

/** Return true if new panel goes AFTER anchor (right / bottom). */
function isAfter(placement: Placement): boolean {
  return placement === "split-right" || placement === "split-bottom";
}

/** Walk the dockbox tree and find the panel + its parent box. Returns null if not found. */
function findPanelAndParent(
  box: BoxBase,
  anchorTabId: string,
  parent: BoxBase | null = null,
): { panel: PanelBase; parentBox: BoxBase | null } | null {
  for (const child of box.children) {
    if ("tabs" in child) {
      // It's a panel
      const panel = child as PanelBase;
      if (panel.tabs?.some(t => t.id === anchorTabId)) {
        return { panel, parentBox: box };
      }
    } else {
      // It's a box, recurse
      const result = findPanelAndParent(child as BoxBase, anchorTabId, box);
      if (result) return result;
    }
  }
  return null;
}

/**
 * Check if a sibling panel already exists on the given edge relative to the anchor tab.
 * Returns the first panel found in the sibling node, or null if none exists.
 * Uses LayoutBase / BoxBase / PanelBase — no parent pointers, JSON-safe.
 */
export function findSiblingPanel(
  layout: LayoutBase,
  anchorTabId: string,
  placement: Placement,
): PanelBase | null {
  const found = findPanelAndParent(layout.dockbox, anchorTabId);
  if (!found || !found.parentBox) return null;

  const { panel, parentBox } = found;
  const mode = placementToMode(placement);

  if (parentBox.mode !== mode) return null;

  const idx = parentBox.children.indexOf(panel as BoxBase | PanelBase);
  if (idx === -1) return null;

  const siblingIdx = isAfter(placement) ? idx + 1 : idx - 1;
  const sibling = parentBox.children[siblingIdx];
  if (!sibling) return null;

  if ("tabs" in sibling) return sibling as PanelBase;

  // Sibling is a box — find first or last panel within it
  const subPanels: PanelBase[] = [];
  const collectSubPanels = (b: BoxBase) => {
    for (const c of b.children) {
      if ("tabs" in c) subPanels.push(c as PanelBase);
      else collectSubPanels(c as BoxBase);
    }
  };
  collectSubPanels(sibling as BoxBase);
  if (subPanels.length === 0) return null;
  return isAfter(placement) ? subPanels[0] : subPanels[subPanels.length - 1];
}

/**
 * Replace a child node within a box's children array with a replacement node.
 * Returns a new box (immutable).
 */
function replaceChildInBox(
  box: BoxBase,
  target: BoxBase | PanelBase,
  replacement: BoxBase | PanelBase,
): BoxBase {
  return {
    ...box,
    children: box.children.map(c => (c === target ? replacement : c)),
  };
}

/**
 * Recursively find and replace a child in the box tree.
 * Returns null if not found.
 */
function replaceChildDeep(
  box: BoxBase,
  target: BoxBase | PanelBase,
  replacement: BoxBase | PanelBase,
): BoxBase | null {
  const idx = box.children.indexOf(target);
  if (idx !== -1) {
    return replaceChildInBox(box, target, replacement);
  }
  for (const child of box.children) {
    if (!("tabs" in child)) {
      const result = replaceChildDeep(child as BoxBase, target, replacement);
      if (result) {
        return {
          ...box,
          children: box.children.map(c => (c === child ? result : c)),
        };
      }
    }
  }
  return null;
}

/**
 * Split the layout by placing newTab in a new panel alongside the panel containing anchorTabId.
 *
 * scope "parent": split within the panel's parent box.
 * scope "root": split at the root dockbox level.
 *
 * All types are JSON-safe (BoxBase / PanelBase) — no parent pointers.
 */
export function splitPanel(
  layout: LayoutBase,
  anchorTabId: string,
  newTab: TabBase,
  placement: Placement,
  scope: Scope,
): LayoutBase {
  const newLayout = cloneLayout(layout);

  const found = findPanelAndParent(newLayout.dockbox, anchorTabId);
  if (!found) return newLayout; // anchor not found — return unchanged

  const { panel: anchorPanel, parentBox } = found;
  const newPanel: PanelBase = {
    tabs: [newTab as TabData],
    activeId: newTab.id,
  };
  const mode = placementToMode(placement);
  const after = isAfter(placement);

  if (scope === "parent" && parentBox) {
    const anchorSize = (anchorPanel as { size?: number }).size ?? 200;
    newPanel.size = anchorSize;

    if (parentBox.mode === mode) {
      // Same axis — splice new panel adjacent to anchor
      const idx = parentBox.children.indexOf(anchorPanel as unknown as BoxBase | PanelBase);
      if (idx === -1) return newLayout;
      const insertAt = after ? idx + 1 : idx;
      parentBox.children.splice(insertAt, 0, newPanel as unknown as BoxBase | PanelBase);
    } else {
      // Different axis — replace anchor panel with a sub-box
      const subBox: BoxBase = {
        mode,
        children: after
          ? [anchorPanel as unknown as BoxBase | PanelBase, newPanel as unknown as BoxBase | PanelBase]
          : [newPanel as unknown as BoxBase | PanelBase, anchorPanel as unknown as BoxBase | PanelBase],
      };
      // Give sub-box the anchor's size
      (subBox as { size?: number }).size = anchorSize;
      // Give each child equal size
      (anchorPanel as { size?: number }).size = 200;
      newPanel.size = 200;
      const idx = parentBox.children.indexOf(anchorPanel as unknown as BoxBase | PanelBase);
      if (idx !== -1) {
        parentBox.children.splice(idx, 1, subBox as unknown as BoxBase | PanelBase);
      }
    }
  } else {
    // scope === "root" — split at the dockbox level
    const rootBox = newLayout.dockbox;
    if (rootBox.mode === mode) {
      // Same axis — splice new panel at the edge
      if (after) {
        rootBox.children.push(newPanel as unknown as BoxBase | PanelBase);
      } else {
        rootBox.children.unshift(newPanel as unknown as BoxBase | PanelBase);
      }
    } else {
      // Different axis — wrap entire dockbox in a new box
      const oldChildren = rootBox.children;
      const oldMode = rootBox.mode;
      const innerBox: BoxBase = { mode: oldMode, children: oldChildren };
      rootBox.mode = mode;
      rootBox.children = after
        ? [innerBox as unknown as BoxBase | PanelBase, newPanel as unknown as BoxBase | PanelBase]
        : [newPanel as unknown as BoxBase | PanelBase, innerBox as unknown as BoxBase | PanelBase];
    }
  }

  return newLayout;
}
